import { useState } from "react";

import { ConfirmationDialog } from "ui/confirmation-dialog";

import {
  useUnsavedChangesNavigation,
  type BlockedNavigation,
} from "contexts/unsaved-changes-navigation-context";
import { useUnsavedChangesNavigationGuard } from "hooks/use-unsaved-changes-navigation-guard";

type UnsavedChangesIntent = { type: "cancel" } | { type: "navigate"; navigation: BlockedNavigation };

export type UseUnsavedChangesGuardOptions = {
  isEditing: boolean;
  isDirty: boolean;
  /** Reset any dirty/draft state and exit edit mode. Called on a confirmed Cancel or navigation. */
  onDiscard: () => void;
};

/**
 * Shared Cancel/navigate-away confirmation for any form that edits in place: shows the same
 * "unsaved changes" dialog whether the user clicks Cancel or navigates away while dirty, and routes
 * both through `onDiscard`. Registers with the app-wide `UnsavedChangesNavigationProvider` guard slot,
 * so it also protects switching away entirely (e.g. to another nav item) while this form is dirty.
 */
export const useUnsavedChangesGuard = ({
  isEditing,
  isDirty,
  onDiscard,
}: UseUnsavedChangesGuardOptions) => {
  const { runWithoutGuard } = useUnsavedChangesNavigation();
  const [intent, setIntent] = useState<UnsavedChangesIntent | null>(null);

  useUnsavedChangesNavigationGuard({
    when: isEditing && isDirty,
    onNavigateAttempt: (navigation) => setIntent({ type: "navigate", navigation }),
  });

  const handleCancelClick = () => {
    if (isDirty) {
      setIntent({ type: "cancel" });
      return;
    }

    onDiscard();
  };

  const handleCloseModal = () => setIntent(null);

  const handleConfirm = () => {
    const current = intent;

    setIntent(null);
    onDiscard();

    if (current?.type === "navigate") {
      runWithoutGuard(current.navigation);
    }
  };

  const unsavedChangesDialog = (
    <ConfirmationDialog
      open={intent !== null}
      onClose={handleCloseModal}
      headline="Are you sure you want to cancel your changes?"
      message="All unsaved fields won't be saved. The form will be reset with last saved values."
      cancelButtonText="Stay in edit mode"
      confirmButtonText="Leave edit mode"
      onConfirm={handleConfirm}
      intent="warning"
    />
  );

  return { handleCancelClick, unsavedChangesDialog };
};
